<template>
  <div class="min-h-screen bg-[#f7f5ef] text-[#1f2937]">
    <section class="mx-auto max-w-6xl px-4 py-10 lg:px-8">
      <div class="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p class="text-sm font-medium text-slate-500">Step 13 of 13</p>
          <div class="mt-3 h-2 w-[320px] max-w-full overflow-hidden rounded-full bg-[#e7e0d2]">
            <div class="h-full w-[57%] rounded-full bg-gradient-to-r from-orange-500 to-orange-300"></div>
          </div>
        </div>
        <div class="inline-flex items-center gap-2 rounded-full border border-[#f5d7b4] bg-[#fff4e6] px-4 py-2 text-sm text-[#9a5b18]">
          <span class="h-2 w-2 rounded-full bg-[#f5a524]"></span>
          Setup 57%
        </div>
      </div>

      <div class="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside class="hidden lg:block">
          <div class="scrollspy">
            <p class="scrollspy-kicker">Onboarding</p>
            <nav class="mt-4 space-y-2">
              <a
                v-for="item in sectionLinks"
                :key="item.id"
                :href="`#${item.id}`"
                class="scrollspy-link"
                :class="{ active: activeSection === item.id }"
              >
                <span class="scrollspy-dot"></span>
                <span>{{ item.label }}</span>
              </a>
            </nav>
          </div>
        </aside>

        <form class="space-y-8" @submit.prevent="submit">
        <section :id="sectionLinks[0].id" class="panel onboard-panel">
          <div class="section-head">
            <div>
              <h2 class="section-title">Create your account</h2>
              <p class="section-copy">Start with the basics. You can finish your profile later.</p>
            </div>
            <div class="status-pill">
              <span class="status-dot"></span>
              Linked to existing place
            </div>
          </div>

          <div class="section-grid">
            <div class="subpanel">
              <h3 class="subpanel-title">Account details</h3>
              <div class="grid gap-4 md:grid-cols-2">
                <label class="field">
                  <span class="field-label">Full name</span>
                  <input v-model.trim="form.fullName" type="text" class="dark-input" placeholder="e.g., Alex Kumar">
                </label>
                <label class="field">
                  <span class="field-label">Email</span>
                  <input v-model.trim="form.email" type="email" class="dark-input" placeholder="e.g., alex@business.com">
                </label>
              </div>
            </div>

            <div class="subpanel">
              <h3 class="subpanel-title">Security</h3>
              <div class="grid gap-4 md:grid-cols-2">
                <label class="field">
                  <span class="field-label">Password</span>
                  <input v-model="form.password" type="password" class="dark-input" placeholder="••••••••">
                </label>
                <label class="field">
                  <span class="field-label">Phone (optional)</span>
                  <input v-model.trim="form.accountPhone" type="text" class="dark-input" placeholder="e.g., +1 214 555 0101">
                </label>
              </div>
            </div>

            <div class="subpanel">
              <h3 class="subpanel-title">What do you offer?</h3>
              <div class="grid gap-4 md:grid-cols-3">
                <label
                  v-for="option in offerOptions"
                  :key="option.value"
                  class="choice-card"
                  :class="{ active: form.offerType === option.value }"
                >
                  <input v-model="form.offerType" type="radio" :value="option.value" class="sr-only">
                  <span class="choice-icon" aria-hidden="true">
                    <i class="ri-check-line" v-if="form.offerType === option.value"></i>
                  </span>
                  <div>
                    <p class="choice-title">{{ option.label }}</p>
                    <p class="choice-copy">Select to include</p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <div class="note-strip">Tip: We'll keep onboarding lightweight. You can publish only after required steps are complete.</div>
        </section>

        <section :id="sectionLinks[1].id" class="panel onboard-panel">
          <div class="section-head">
            <div>
              <h2 class="section-title">Business basics</h2>
              <p class="section-copy">This helps customers and the platform identify your business.</p>
            </div>
            <div class="status-pill">
              <span class="status-dot"></span>
              Linked to existing place
            </div>
          </div>

          <div class="section-grid">
            <div class="subpanel">
              <h3 class="subpanel-title">Business identity</h3>
              <div class="grid gap-4 md:grid-cols-2">
                <label class="field">
                  <span class="field-label">Login email ID</span>
                  <input v-model.trim="form.loginEmailId" type="email" class="dark-input" placeholder="e.g., owner@venue.com">
                </label>
                <label class="field">
                  <span class="field-label">Brand name*</span>
                  <input v-model.trim="form.brandName" type="text" class="dark-input" placeholder="e.g., WedMe Grand Palace">
                </label>
                <label class="field">
                  <span class="field-label">Business display name</span>
                  <input v-model.trim="form.businessDisplayName" type="text" class="dark-input" placeholder="e.g., Grand Palace Events">
                </label>
                <label class="field">
                  <span class="field-label">Contact person name</span>
                  <input v-model.trim="form.contactPersonName" type="text" class="dark-input" placeholder="e.g., Alex Kumar">
                </label>
              </div>
            </div>

            <div class="subpanel">
              <h3 class="subpanel-title">Contact</h3>
              <div class="grid gap-4 md:grid-cols-2">
                <label class="field">
                  <span class="field-label">Contact number*</span>
                  <input v-model.trim="form.businessPhone" type="text" class="dark-input" placeholder="e.g., +1 214 555 0101">
                </label>
                <label class="field">
                  <span class="field-label">Additional email ID</span>
                  <input v-model.trim="form.additionalEmailId" type="email" class="dark-input" placeholder="e.g., bookings@venue.com">
                </label>
                <label class="field">
                  <span class="field-label">Business email</span>
                  <input v-model.trim="form.businessEmail" type="email" class="dark-input" placeholder="e.g., bookings@grandpalace.com">
                </label>
                <label class="field">
                  <span class="field-label">WhatsApp number</span>
                  <input v-model.trim="form.whatsappNumber" type="text" class="dark-input" placeholder="e.g., +1 214 555 0199">
                </label>
              </div>
            </div>

            <div class="subpanel">
              <h3 class="subpanel-title">Primary area</h3>
              <div class="grid gap-4 md:grid-cols-3">
                <label class="field">
                  <span class="field-label">City (Choose your base city here)</span>
                  <input v-model.trim="form.city" type="text" class="dark-input" placeholder="e.g., Frisco">
                </label>
                <label class="field">
                  <span class="field-label">State</span>
                  <input v-model.trim="form.state" type="text" class="dark-input" placeholder="e.g., TX">
                </label>
                <label class="field">
                  <span class="field-label">Website (optional)</span>
                  <input v-model.trim="form.website" type="text" class="dark-input" placeholder="e.g., https://grandpalace.com">
                </label>
                <label class="field md:col-span-3">
                  <span class="field-label">Address</span>
                  <input v-model.trim="form.address" type="text" class="dark-input" placeholder="e.g., 123 Main St, Frisco, TX">
                </label>
              </div>
            </div>

            <div class="subpanel">
              <h3 class="subpanel-title">Social presence</h3>
              <div class="grid gap-4 md:grid-cols-2">
                <label class="field">
                  <span class="field-label">Website link</span>
                  <input v-model.trim="form.websiteLink" type="text" class="dark-input" placeholder="https://yourvenue.com">
                </label>
                <label class="field">
                  <span class="field-label">Facebook url</span>
                  <input v-model.trim="form.facebookUrl" type="text" class="dark-input" placeholder="https://facebook.com/yourvenue">
                </label>
                <label class="field">
                  <span class="field-label">Instagram url</span>
                  <input v-model.trim="form.instagramUrl" type="text" class="dark-input" placeholder="https://instagram.com/yourvenue">
                </label>
                <label class="field">
                  <span class="field-label">Youtube/Vimeo url</span>
                  <input v-model.trim="form.youtubeVimeoUrl" type="text" class="dark-input" placeholder="https://youtube.com/yourvenue">
                </label>
              </div>
            </div>
          </div>

          <div class="note-strip">You can add more venues/locations later. This is just your starting point.</div>
        </section>

        <section :id="sectionLinks[2].id" class="panel onboard-panel">
          <div class="section-head">
            <div>
              <h2 class="section-title">Link your place</h2>
              <p class="section-copy">Choose an existing place (if other businesses operate there) or add a new one.</p>
            </div>
            <div class="status-pill">
              <span class="status-dot"></span>
              Linked to existing place
            </div>
          </div>

          <div class="section-grid">
            <div class="subpanel">
              <h3 class="subpanel-title">Choose how to link your place</h3>
              <div class="grid gap-3">
                <label class="choice-row" :class="{ active: form.placeMode === 'existing' }">
                  <input v-model="form.placeMode" type="radio" value="existing" class="sr-only">
                  <span class="radio-indicator"><span class="radio-dot"></span></span>
                  <span>Find existing place (if other businesses operate here)</span>
                </label>
                <label class="choice-row" :class="{ active: form.placeMode === 'new' }">
                  <input v-model="form.placeMode" type="radio" value="new" class="sr-only">
                  <span class="radio-indicator"><span class="radio-dot"></span></span>
                  <span>Add new place</span>
                </label>
              </div>
            </div>

            <div class="subpanel">
              <h3 class="subpanel-title">Find a place</h3>
              <label class="field">
                <span class="field-label">Search</span>
                <input v-model.trim="form.placeSearch" type="text" class="dark-input" placeholder="Type venue name or address">
              </label>

              <div class="mt-4 space-y-3">
                <div
                  v-for="place in places"
                  :key="place.name"
                  class="list-card"
                  :class="{ active: form.selectedPlace === place.name }"
                >
                  <div>
                    <p class="choice-title">{{ place.name }}</p>
                    <p class="choice-copy">{{ place.meta }}</p>
                  </div>
                  <button
                    type="button"
                    class="mini-btn"
                    :class="{ active: form.selectedPlace === place.name }"
                    @click="form.selectedPlace = place.name"
                  >
                    {{ form.selectedPlace === place.name ? 'Selected' : 'Select' }}
                  </button>
                </div>
              </div>

              <div class="mt-4 grid gap-4 md:grid-cols-2">
                <label class="field">
                  <span class="field-label">Relationship type</span>
                  <div class="grid gap-3 sm:grid-cols-3">
                    <label
                      v-for="option in relationshipOptions"
                      :key="option"
                      class="choice-card compact"
                      :class="{ active: form.relationshipType === option }"
                    >
                      <input v-model="form.relationshipType" type="radio" :value="option" class="sr-only">
                      <span class="choice-icon" aria-hidden="true">
                        <i class="ri-check-line" v-if="form.relationshipType === option"></i>
                      </span>
                      <div>
                        <p class="choice-title">{{ option }}</p>
                      </div>
                    </label>
                  </div>
                </label>
                <label class="field">
                  <span class="field-label">Unit / Suite / Wing (optional)</span>
                  <input v-model.trim="form.unit" type="text" class="dark-input" placeholder="e.g., Wing A, Suite 210">
                </label>
              </div>

              <label class="toggle-card mt-4" :class="{ active: form.primaryLocation }">
                <input v-model="form.primaryLocation" type="checkbox" class="sr-only">
                <span class="choice-icon" aria-hidden="true">
                  <i class="ri-check-line" v-if="form.primaryLocation"></i>
                </span>
                <div>
                  <p class="choice-title">Primary location</p>
                  <p class="choice-copy">Use this for default routing & dashboards.</p>
                </div>
              </label>
            </div>
          </div>

          <div class="note-strip">This supports multiple businesses at the same address. Your business links to the Place via a relationship (Owner/Tenant/Operator).</div>
        </section>

        <section :id="sectionLinks[3].id" class="panel onboard-panel">
          <div class="section-head">
            <div>
              <h2 class="section-title">Create your venue listing</h2>
              <p class="section-copy">Public listing details (name, type, description, contact).</p>
            </div>
            <div class="status-pill">
              <span class="status-dot"></span>
              Linked to existing place
            </div>
          </div>

          <div class="section-grid">
            <div class="subpanel">
              <h3 class="subpanel-title">Venue listing details</h3>
              <div class="grid gap-4 md:grid-cols-2">
                <label class="field">
                  <span class="field-label">Venue name</span>
                  <input v-model.trim="form.venueName" type="text" class="dark-input" placeholder="e.g., Grand Palace – Ballroom & Lawn">
                </label>
                <label class="field">
                  <span class="field-label">Venue type</span>
                  <div class="grid gap-3 sm:grid-cols-2">
                    <label
                      v-for="type in venueTypeOptions"
                      :key="type"
                      class="choice-card compact"
                      :class="{ active: form.venueType === type }"
                    >
                      <input v-model="form.venueType" type="radio" :value="type" class="sr-only">
                      <span class="choice-icon" aria-hidden="true">
                        <i class="ri-check-line" v-if="form.venueType === type"></i>
                      </span>
                      <div><p class="choice-title">{{ type }}</p></div>
                    </label>
                  </div>
                </label>
                <label class="field">
                  <span class="field-label">Short description</span>
                  <input v-model.trim="form.shortDescription" type="text" class="dark-input" placeholder="One-line value proposition">
                </label>
                <label class="field">
                  <span class="field-label">One line bio for your venue</span>
                  <input v-model.trim="form.oneLineVenueBio" type="text" class="dark-input" placeholder="Elegant lakeside venue for large celebrations">
                </label>
                <label class="field">
                  <span class="field-label">Full description</span>
                  <textarea v-model.trim="form.fullDescription" rows="4" class="dark-input dark-textarea" placeholder="Tell guests what makes this venue special."></textarea>
                </label>
                <label class="field">
                  <span class="field-label">USP (max 230 characters)</span>
                  <textarea v-model.trim="form.usp" rows="4" maxlength="230" class="dark-input dark-textarea" placeholder="Share what makes your venue stand out."></textarea>
                </label>
              </div>
            </div>

            <div class="subpanel">
              <h3 class="subpanel-title">Public contact (can differ from business contact)</h3>
              <div class="grid gap-4 md:grid-cols-2">
                <label class="field">
                  <span class="field-label">Public phone</span>
                  <input v-model.trim="form.publicPhone" type="text" class="dark-input" placeholder="e.g., +1 214 555 0101">
                </label>
                <label class="field">
                  <span class="field-label">Public email</span>
                  <input v-model.trim="form.publicEmail" type="email" class="dark-input" placeholder="e.g., events@grandpalace.com">
                </label>
                <label class="field">
                  <span class="field-label">Website (optional)</span>
                  <input v-model.trim="form.publicWebsite" type="text" class="dark-input" placeholder="e.g., https://grandpalace.com">
                </label>
                <div class="field">
                  <span class="field-label">Event types supported</span>
                  <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    <label
                      v-for="type in eventTypes"
                      :key="type"
                      class="choice-card compact"
                      :class="{ active: form.eventTypes.includes(type) }"
                    >
                      <input v-model="form.eventTypes" type="checkbox" :value="type" class="sr-only">
                      <span class="choice-icon" aria-hidden="true">
                        <i class="ri-check-line" v-if="form.eventTypes.includes(type)"></i>
                      </span>
                      <div>
                        <p class="choice-title">{{ type }}</p>
                        <p class="choice-copy">Select</p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="note-strip">Tip: Keep the venue listing brand-consistent. Spaces can have their own photos/capacity/pricing later.</div>
        </section>

        <section :id="sectionLinks[4].id" class="panel onboard-panel">
          <div class="section-head">
            <div>
              <h2 class="section-title">Rules & logistics</h2>
              <p class="section-copy">Set policies (catering/alcohol/noise) and key logistics (parking/access).</p>
            </div>
            <div class="status-pill">
              <span class="status-dot"></span>
              Linked to existing place
            </div>
          </div>

          <div class="section-grid">
            <div class="subpanel">
              <h3 class="subpanel-title">Policies</h3>
              <div class="grid gap-4 md:grid-cols-2">
                <div class="field md:col-span-2">
                  <span class="field-label">What is your policy on catering?</span>
                  <div class="grid gap-3 sm:grid-cols-2">
                    <label v-for="option in cateringPolicyOptions" :key="option" class="choice-card compact" :class="{ active: form.cateringPolicy === option }">
                      <input v-model="form.cateringPolicy" type="radio" :value="option" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.cateringPolicy === option"></i></span>
                      <div><p class="choice-title">{{ option }}</p></div>
                    </label>
                  </div>
                </div>
                <div class="field">
                  <span class="field-label">Outside catering allowed?</span>
                  <div class="grid gap-3 sm:grid-cols-2">
                    <label v-for="option in yesNoOptions" :key="`outside-${option}`" class="choice-card compact" :class="{ active: form.outsideCatering === option }">
                      <input v-model="form.outsideCatering" type="radio" :value="option" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.outsideCatering === option"></i></span>
                      <div><p class="choice-title">{{ option }}</p></div>
                    </label>
                  </div>
                </div>
                <div class="field">
                  <span class="field-label">In-house catering available?</span>
                  <div class="grid gap-3 sm:grid-cols-2">
                    <label v-for="option in yesNoOptions" :key="`inhouse-${option}`" class="choice-card compact" :class="{ active: form.inhouseCatering === option }">
                      <input v-model="form.inhouseCatering" type="radio" :value="option" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.inhouseCatering === option"></i></span>
                      <div><p class="choice-title">{{ option }}</p></div>
                    </label>
                  </div>
                </div>
                <div class="field">
                  <span class="field-label">Alcohol policy</span>
                  <div class="grid gap-3 sm:grid-cols-3">
                    <label v-for="option in alcoholOptions" :key="option" class="choice-card compact" :class="{ active: form.alcoholPolicy === option }">
                      <input v-model="form.alcoholPolicy" type="radio" :value="option" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.alcoholPolicy === option"></i></span>
                      <div><p class="choice-title">{{ option }}</p></div>
                    </label>
                  </div>
                </div>
                <label class="field">
                  <span class="field-label">Noise cutoff time</span>
                  <input v-model.trim="form.noiseCutoff" type="text" class="dark-input" placeholder="11:00 PM">
                </label>
                <div class="field md:col-span-2">
                  <span class="field-label">What is your policy on decor?</span>
                  <div class="grid gap-3 sm:grid-cols-3">
                    <label v-for="option in decorPolicyOptions" :key="option" class="choice-card compact" :class="{ active: form.decorPolicy === option }">
                      <input v-model="form.decorPolicy" type="radio" :value="option" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.decorPolicy === option"></i></span>
                      <div><p class="choice-title">{{ option }}</p></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div class="subpanel">
              <h3 class="subpanel-title">Logistics</h3>
              <div class="grid gap-4 md:grid-cols-2">
                <div class="field">
                  <span class="field-label">Parking available?</span>
                  <div class="grid gap-3 sm:grid-cols-2">
                    <label v-for="option in yesNoOptions" :key="`parking-${option}`" class="choice-card compact" :class="{ active: form.parkingAvailable === option }">
                      <input v-model="form.parkingAvailable" type="radio" :value="option" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.parkingAvailable === option"></i></span>
                      <div><p class="choice-title">{{ option }}</p></div>
                    </label>
                  </div>
                </div>
                <div class="field">
                  <span class="field-label">Parking type</span>
                  <div class="grid gap-3 sm:grid-cols-3">
                    <label v-for="option in parkingTypeOptions" :key="option" class="choice-card compact" :class="{ active: form.parkingType === option }">
                      <input v-model="form.parkingType" type="radio" :value="option" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.parkingType === option"></i></span>
                      <div><p class="choice-title">{{ option }}</p></div>
                    </label>
                  </div>
                </div>
                <div class="field">
                  <span class="field-label">Wheelchair accessible?</span>
                  <div class="grid gap-3 sm:grid-cols-2">
                    <label v-for="option in yesNoOptions" :key="`wheelchair-${option}`" class="choice-card compact" :class="{ active: form.wheelchairAccessible === option }">
                      <input v-model="form.wheelchairAccessible" type="radio" :value="option" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.wheelchairAccessible === option"></i></span>
                      <div><p class="choice-title">{{ option }}</p></div>
                    </label>
                  </div>
                </div>
                <div class="field">
                  <span class="field-label">Decoration restrictions (tags)</span>
                  <div class="grid gap-3 sm:grid-cols-2">
                    <label
                      v-for="tag in decorationTags"
                      :key="tag"
                      class="choice-card compact"
                      :class="{ active: form.decorationRestrictions.includes(tag) }"
                    >
                      <input v-model="form.decorationRestrictions" type="checkbox" :value="tag" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.decorationRestrictions.includes(tag)"></i></span>
                      <div>
                        <p class="choice-title">{{ tag }}</p>
                        <p class="choice-copy">Select</p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div class="subpanel">
              <h3 class="subpanel-title">Notes (optional)</h3>
              <label class="field">
                <span class="field-label">Internal notes</span>
                <textarea v-model.trim="form.internalNotes" rows="4" class="dark-input dark-textarea" placeholder="Anything your team should remember (not public)."></textarea>
              </label>
            </div>
          </div>
        </section>

        <section :id="sectionLinks[5].id" class="panel onboard-panel">
          <div class="section-head">
            <div>
              <h2 class="section-title">Add spaces</h2>
              <p class="section-copy">Add your halls/lawn/rooftop. Supports divisible halls with layout options.</p>
            </div>
            <div class="status-pill">
              <span class="status-dot"></span>
              Linked to existing place
            </div>
          </div>
          <div class="note-strip">Add at least one space. If a hall can be split/combined, mark it as divisible and create layout options (bookable configurations).</div>
          <div class="subpanel">
            <div class="flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-[#22345f] bg-[#111823] p-5">
              <div>
                <p class="choice-title">Space 1</p>
                <p class="choice-copy">Type: Hall • Indoor</p>
                <p class="choice-copy">Capacity: — seated / — standing</p>
              </div>
              <div class="flex gap-3">
                <button type="button" class="mini-btn">Edit</button>
                <button type="button" class="mini-btn danger">Delete</button>
              </div>
            </div>
            <button type="button" class="gradient-btn mt-4">+ Add Space</button>
          </div>
        </section>

        <section :id="sectionLinks[6].id" class="panel onboard-panel">
          <div class="section-head">
            <div>
              <h2 class="section-title">Availability setup</h2>
              <p class="section-copy">Slot-based availability with overnight + optional custom time requests.</p>
            </div>
            <div class="status-pill">
              <span class="status-dot"></span>
              Linked to existing place
            </div>
          </div>
          <div class="subpanel">
            <h3 class="subpanel-title">Availability mode</h3>
            <div class="field">
              <span class="field-label">Mode</span>
              <div class="grid gap-3 sm:grid-cols-3">
                <label
                  v-for="mode in availabilityModes"
                  :key="mode"
                  class="choice-card compact"
                  :class="{ active: form.availabilityMode === mode }"
                >
                  <input v-model="form.availabilityMode" type="radio" :value="mode" class="sr-only">
                  <span class="choice-icon"><i class="ri-check-line" v-if="form.availabilityMode === mode"></i></span>
                  <div><p class="choice-title">{{ mode }}</p></div>
                </label>
              </div>
            </div>
          </div>
          <div class="note-strip">FULL_DAY means a date is either available or not. Slot templates are not used.</div>
          <div class="note-strip">Important: Overnight slots are supported when end time is earlier than start time (e.g., 23:00 → 06:00).</div>
        </section>

        <section :id="sectionLinks[7].id" class="panel onboard-panel">
          <div class="section-head">
            <div>
              <h2 class="section-title">Pricing setup</h2>
              <p class="section-copy">Venue defaults + space/configuration overrides.</p>
            </div>
            <div class="status-pill">
              <span class="status-dot"></span>
              Linked to existing place
            </div>
          </div>

          <div class="section-grid">
            <div class="subpanel">
              <h3 class="subpanel-title">Pricing approach</h3>
              <label class="toggle-card" :class="{ active: form.sharedPricing }">
                <input v-model="form.sharedPricing" type="checkbox" class="sr-only">
                <span class="choice-icon"><i class="ri-check-line" v-if="form.sharedPricing"></i></span>
                <div>
                  <p class="choice-title">All spaces share the same pricing (venue default)</p>
                  <p class="choice-copy">Toggle on/off</p>
                </div>
              </label>
            </div>

            <div class="subpanel">
              <h3 class="subpanel-title">Venue default pricing (optional)</h3>
              <div class="grid gap-4 md:grid-cols-2">
                <div class="field md:col-span-2">
                  <span class="field-label">Does your venue have rental cost along with per plate cost?</span>
                  <div class="grid gap-3 sm:grid-cols-2">
                    <label v-for="option in yesNoOptions" :key="`rental-${option}`" class="choice-card compact" :class="{ active: form.rentalCostWithPlate === option }">
                      <input v-model="form.rentalCostWithPlate" type="radio" :value="option" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.rentalCostWithPlate === option"></i></span>
                      <div><p class="choice-title">{{ option }}</p></div>
                    </label>
                  </div>
                </div>
                <div class="field">
                  <span class="field-label">Pricing model</span>
                  <div class="grid gap-3 sm:grid-cols-3">
                    <label
                      v-for="option in pricingModels"
                      :key="option"
                      class="choice-card compact"
                      :class="{ active: form.defaultPricingModel === option }"
                    >
                      <input v-model="form.defaultPricingModel" type="radio" :value="option" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.defaultPricingModel === option"></i></span>
                      <div><p class="choice-title">{{ option }}</p></div>
                    </label>
                  </div>
                </div>
                <label class="field">
                  <span class="field-label">Base price / starting from</span>
                  <input v-model.trim="form.basePrice" type="text" class="dark-input" placeholder="e.g., $3000">
                </label>
                <label class="field">
                  <span class="field-label">Starting price for vegetarian menu (assume 250 pax and standard menu)</span>
                  <input v-model.trim="form.vegMenuPrice" type="text" class="dark-input" placeholder="e.g., 1200">
                </label>
                <label class="field">
                  <span class="field-label">Starting price for non-veg menu (assume 250 pax and standard menu)</span>
                  <input v-model.trim="form.nonVegMenuPrice" type="text" class="dark-input" placeholder="e.g., 1500">
                </label>
                <label class="field">
                  <span class="field-label">Additional hour rate (optional)</span>
                  <input v-model.trim="form.additionalHourRate" type="text" class="dark-input" placeholder="e.g., $250/hr">
                </label>
                <label class="field">
                  <span class="field-label">Weekend/peak note (optional)</span>
                  <input v-model.trim="form.weekendNote" type="text" class="dark-input" placeholder="e.g., +15% on Sat">
                </label>
              </div>
            </div>

            <div class="note-strip">Set pricing per space. For divisible halls, set pricing per layout option (configuration).</div>

            <div class="subpanel">
              <h3 class="subpanel-title">Space</h3>
              <div class="grid gap-4 md:grid-cols-2">
                <label class="toggle-card" :class="{ active: form.useVenueDefault }">
                  <input v-model="form.useVenueDefault" type="checkbox" class="sr-only">
                  <span class="choice-icon"><i class="ri-check-line" v-if="form.useVenueDefault"></i></span>
                  <div>
                    <p class="choice-title">Use venue default</p>
                    <p class="choice-copy">If off, set custom pricing here.</p>
                  </div>
                </label>
                <div class="field">
                  <span class="field-label">Pricing model</span>
                  <div class="grid gap-3 sm:grid-cols-3">
                    <label
                      v-for="option in pricingModels"
                      :key="`space-${option}`"
                      class="choice-card compact"
                      :class="{ active: form.spacePricingModel === option }"
                    >
                      <input v-model="form.spacePricingModel" type="radio" :value="option" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.spacePricingModel === option"></i></span>
                      <div><p class="choice-title">{{ option }}</p></div>
                    </label>
                  </div>
                </div>
              </div>

              <div class="mt-4 grid gap-4 md:grid-cols-3">
                <label class="field">
                  <span class="field-label">Morning</span>
                  <input v-model.trim="form.morningPrice" type="text" class="dark-input" placeholder="$">
                </label>
                <label class="field">
                  <span class="field-label">Evening</span>
                  <input v-model.trim="form.eveningPrice" type="text" class="dark-input" placeholder="$">
                </label>
                <label class="field">
                  <span class="field-label">Overnight</span>
                  <input v-model.trim="form.overnightPrice" type="text" class="dark-input" placeholder="$">
                </label>
                <label class="field">
                  <span class="field-label">Per event (if used)</span>
                  <input v-model.trim="form.perEventPrice" type="text" class="dark-input" placeholder="$">
                </label>
                <label class="field">
                  <span class="field-label">Additional hour rate</span>
                  <input v-model.trim="form.spaceAdditionalHourRate" type="text" class="dark-input" placeholder="$ / hr">
                </label>
              </div>
            </div>
          </div>
        </section>

        <section :id="sectionLinks[8].id" class="panel onboard-panel">
          <div class="section-head">
            <div>
              <h2 class="section-title">Deposit & cancellation</h2>
              <p class="section-copy">Risk-free deposit windows build trust and improve conversions.</p>
            </div>
            <div class="status-pill">
              <span class="status-dot"></span>
              Linked to existing place
            </div>
          </div>

          <div class="section-grid">
            <div class="subpanel">
              <h3 class="subpanel-title">Deposit settings</h3>
              <div class="grid gap-4 md:grid-cols-2">
                <label class="toggle-card" :class="{ active: form.depositRequired }">
                  <input v-model="form.depositRequired" type="checkbox" class="sr-only">
                  <span class="choice-icon"><i class="ri-check-line" v-if="form.depositRequired"></i></span>
                  <div>
                    <p class="choice-title">Deposit required</p>
                    <p class="choice-copy">Toggle on/off</p>
                  </div>
                </label>
                <div class="field">
                  <span class="field-label">Deposit type</span>
                  <div class="grid gap-3 sm:grid-cols-2">
                    <label
                      v-for="option in depositTypes"
                      :key="option"
                      class="choice-card compact"
                      :class="{ active: form.depositType === option }"
                    >
                      <input v-model="form.depositType" type="radio" :value="option" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.depositType === option"></i></span>
                      <div><p class="choice-title">{{ option }}</p></div>
                    </label>
                  </div>
                </div>
                <label class="field">
                  <span class="field-label">Booking amount (in percentage terms) to block a date</span>
                  <input v-model.trim="form.depositAmount" type="text" class="dark-input" placeholder="20">
                </label>
                <label class="field">
                  <span class="field-label">How many weeks in advance should a booking be made?</span>
                  <input v-model.trim="form.bookingLeadWeeks" type="text" class="dark-input" placeholder="e.g., 8">
                </label>
              </div>
            </div>

            <div class="subpanel">
              <h3 class="subpanel-title">Risk-free policy (trust builder)</h3>
              <div class="grid gap-4 md:grid-cols-2">
                <label class="toggle-card" :class="{ active: form.riskFreeRefund }">
                  <input v-model="form.riskFreeRefund" type="checkbox" class="sr-only">
                  <span class="choice-icon"><i class="ri-check-line" v-if="form.riskFreeRefund"></i></span>
                  <div>
                    <p class="choice-title">Enable risk-free refund window</p>
                    <p class="choice-copy">Toggle on/off</p>
                  </div>
                </label>
                <label class="field">
                  <span class="field-label">Full refund if cancelled ≥ (days)</span>
                  <input v-model.trim="form.fullRefundDays" type="text" class="dark-input" placeholder="30">
                </label>
                <label class="toggle-card" :class="{ active: form.priceLockAfterBooking }">
                  <input v-model="form.priceLockAfterBooking" type="checkbox" class="sr-only">
                  <span class="choice-icon"><i class="ri-check-line" v-if="form.priceLockAfterBooking"></i></span>
                  <div>
                    <p class="choice-title">Price lock after booking</p>
                    <p class="choice-copy">Toggle on/off</p>
                  </div>
                </label>
                <div class="field md:col-span-2">
                  <span class="field-label">Cancellation policy if a user initiates cancellation</span>
                  <div class="grid gap-3 sm:grid-cols-2">
                    <label v-for="option in userCancellationOptions" :key="option" class="choice-card compact" :class="{ active: form.userCancellationPolicy === option }">
                      <input v-model="form.userCancellationPolicy" type="radio" :value="option" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.userCancellationPolicy === option"></i></span>
                      <div><p class="choice-title">{{ option }}</p></div>
                    </label>
                  </div>
                </div>
                <div class="field md:col-span-2">
                  <span class="field-label">Cancellation policy if you initiate a cancellation</span>
                  <div class="grid gap-3 sm:grid-cols-3">
                    <label v-for="option in hostCancellationOptions" :key="option" class="choice-card compact" :class="{ active: form.hostCancellationPolicy === option }">
                      <input v-model="form.hostCancellationPolicy" type="radio" :value="option" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.hostCancellationPolicy === option"></i></span>
                      <div><p class="choice-title">{{ option }}</p></div>
                    </label>
                  </div>
                </div>
                <label class="field md:col-span-2">
                  <span class="field-label">Terms & conditions of your cancellation policy</span>
                  <textarea v-model.trim="form.cancellationTerms" rows="4" class="dark-input dark-textarea" placeholder="Please describe in detail, e.g. no refunds within a month of the wedding day or 50% amount refundable."></textarea>
                </label>
              </div>
            </div>
          </div>

          <div class="note-strip">Tip: Show exact cut-off dates during checkout (e.g., 'Free cancellation until Mar 12, 11:59 PM').</div>
        </section>

        <section :id="sectionLinks[9].id" class="panel onboard-panel">
          <div class="section-head">
            <div>
              <h2 class="section-title">Additional information</h2>
              <p class="section-copy">Extra venue details that help guests understand stay, accommodation, and booking conditions.</p>
            </div>
            <div class="status-pill">
              <span class="status-dot"></span>
              Linked to existing place
            </div>
          </div>

          <div class="note-strip">To update your description, please send a mail to `vendors@wedmegood.com`.</div>

          <div class="section-grid">
            <div class="subpanel">
              <h3 class="subpanel-title">Accommodation & wedding conditions</h3>
              <div class="grid gap-4">
                <div class="field">
                  <span class="field-label">What spaces are available to host wedding events?</span>
                  <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <label
                      v-for="item in weddingSpaceOptions"
                      :key="item"
                      class="choice-card compact"
                      :class="{ active: form.weddingSpaces.includes(item) }"
                    >
                      <input v-model="form.weddingSpaces" type="checkbox" :value="item" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.weddingSpaces.includes(item)"></i></span>
                      <div><p class="choice-title">{{ item }}</p></div>
                    </label>
                  </div>
                </div>
                <div class="field">
                  <span class="field-label">Do you need a minimum guarantee of room booking for hosting a wedding?</span>
                  <div class="grid gap-3">
                    <label v-for="option in roomGuaranteeOptions" :key="option" class="choice-row" :class="{ active: form.roomGuaranteePolicy === option }">
                      <input v-model="form.roomGuaranteePolicy" type="radio" :value="option" class="sr-only">
                      <span class="radio-indicator"><span class="radio-dot"></span></span>
                      <span>{{ option }}</span>
                    </label>
                  </div>
                </div>
                <div class="grid gap-4 md:grid-cols-3">
                  <label class="field">
                    <span class="field-label">What is the starting price for a basic room at your hotel?</span>
                    <input v-model.trim="form.basicRoomPrice" type="text" class="dark-input" placeholder="e.g., 4500">
                  </label>
                  <label class="field">
                    <span class="field-label">How many rooms are available in your accommodation?</span>
                    <input v-model.trim="form.accommodationRoomsAvailable" type="text" class="dark-input" placeholder="e.g., 120">
                  </label>
                  <label class="field">
                    <span class="field-label">How many rooms are included in your destination price (per night)?</span>
                    <input v-model.trim="form.roomsIncludedInDestinationPrice" type="text" class="dark-input" placeholder="100">
                  </label>
                  <div class="field">
                    <span class="field-label">Do you also allow small size gatherings (&lt;50)?</span>
                    <div class="grid gap-3 sm:grid-cols-2">
                      <label v-for="option in yesNoOptions" :key="`small-${option}`" class="choice-card compact" :class="{ active: form.smallGatheringsAllowed === option }">
                        <input v-model="form.smallGatheringsAllowed" type="radio" :value="option" class="sr-only">
                        <span class="choice-icon"><i class="ri-check-line" v-if="form.smallGatheringsAllowed === option"></i></span>
                        <div><p class="choice-title">{{ option }}</p></div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="subpanel">
              <h3 class="subpanel-title">Venue applicability & operations</h3>
              <div class="grid gap-4">
                <div class="field">
                  <span class="field-label">Please select whatever is applicable for your venue</span>
                  <div class="grid gap-3 sm:grid-cols-2">
                    <label
                      v-for="item in venueApplicabilityOptions"
                      :key="item"
                      class="choice-card compact"
                      :class="{ active: form.venueApplicability.includes(item) }"
                    >
                      <input v-model="form.venueApplicability" type="checkbox" :value="item" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.venueApplicability.includes(item)"></i></span>
                      <div><p class="choice-title">{{ item }}</p></div>
                    </label>
                  </div>
                </div>
                <div class="grid gap-4 md:grid-cols-2">
                  <label class="field">
                    <span class="field-label">What year did your venue start operations?</span>
                    <input v-model.trim="form.operationsStartYear" type="text" class="dark-input" placeholder="e.g., 2016">
                  </label>
                  <div class="field">
                    <span class="field-label">Is parking available at the venue?</span>
                    <div class="grid gap-3">
                      <label v-for="option in parkingAvailabilityDetailOptions" :key="option" class="choice-row" :class="{ active: form.parkingAvailabilityDetail === option }">
                        <input v-model="form.parkingAvailabilityDetail" type="radio" :value="option" class="sr-only">
                        <span class="radio-indicator"><span class="radio-dot"></span></span>
                        <span>{{ option }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="subpanel">
              <h3 class="subpanel-title">Alcohol, décor & entertainment</h3>
              <div class="grid gap-4">
                <div class="field">
                  <span class="field-label">What is your policy on alcohol?</span>
                  <div class="grid gap-3 sm:grid-cols-2">
                    <label v-for="option in alcoholDetailedOptions" :key="option" class="choice-card compact" :class="{ active: form.alcoholDetailedPolicy === option }">
                      <input v-model="form.alcoholDetailedPolicy" type="radio" :value="option" class="sr-only">
                      <span class="choice-icon"><i class="ri-check-line" v-if="form.alcoholDetailedPolicy === option"></i></span>
                      <div><p class="choice-title">{{ option }}</p></div>
                    </label>
                  </div>
                </div>
                <div class="grid gap-4 md:grid-cols-2">
                  <label class="field">
                    <span class="field-label">What is the minimum starting price to decorate your venue?</span>
                    <input v-model.trim="form.minimumDecorPrice" type="text" class="dark-input" placeholder="e.g., 25000">
                  </label>
                  <div class="field">
                    <span class="field-label">What is your policy on DJ's?</span>
                    <div class="grid gap-3">
                      <label v-for="option in djPolicyOptions" :key="option" class="choice-row" :class="{ active: form.djPolicy === option }">
                        <input v-model="form.djPolicy" type="radio" :value="option" class="sr-only">
                        <span class="radio-indicator"><span class="radio-dot"></span></span>
                        <span>{{ option }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section :id="sectionLinks[10].id" class="panel onboard-panel">
          <div class="section-head">
            <div>
              <h2 class="section-title">Add-ons & services</h2>
              <p class="section-copy">Catering, décor, rooms, equipment rentals, link to venue/spaces.</p>
            </div>
            <div class="status-pill">
              <span class="status-dot"></span>
              Linked to existing place
            </div>
          </div>

          <div class="note-strip">Add-ons can be offered by this business or (future) a partner business operating at the same Place.</div>

          <div class="section-grid">
            <div class="subpanel">
              <h3 class="subpanel-title">Select add-ons you offer</h3>
              <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
                <label
                  v-for="item in addonOptions"
                  :key="item"
                  class="choice-card compact"
                  :class="{ active: form.addons.includes(item) }"
                >
                  <input v-model="form.addons" type="checkbox" :value="item" class="sr-only">
                  <span class="choice-icon"><i class="ri-check-line" v-if="form.addons.includes(item)"></i></span>
                  <div>
                    <p class="choice-title">{{ item }}</p>
                    <p class="choice-copy">Select</p>
                  </div>
                </label>
              </div>
            </div>

            <div class="subpanel">
              <h3 class="subpanel-title">Add-on items (optional details)</h3>
              <div class="note-strip inside">No add-on items created yet. Add a specific item like 'Catering – Silver Package' or 'Projector Rental'.</div>
              <button type="button" class="gradient-btn mt-4">+ Add add-on item</button>
            </div>
          </div>
        </section>

        <section :id="sectionLinks[11].id" class="panel onboard-panel">
          <div class="section-head">
            <div>
              <h2 class="section-title">Photos & documents</h2>
              <p class="section-copy">Upload cover and space photos. Floor plan/menu PDFs optional.</p>
            </div>
            <div class="status-pill">
              <span class="status-dot"></span>
              Linked to existing place
            </div>
          </div>

          <div class="note-strip">Prototype uses toggles instead of real uploads.</div>

          <div class="subpanel">
            <h3 class="subpanel-title">Uploads</h3>
            <div class="grid gap-3">
              <label v-for="item in uploadOptions" :key="item" class="toggle-card" :class="{ active: form.uploads.includes(item) }">
                <input v-model="form.uploads" type="checkbox" :value="item" class="sr-only">
                <span class="choice-icon"><i class="ri-check-line" v-if="form.uploads.includes(item)"></i></span>
                <div>
                  <p class="choice-title">{{ item }}</p>
                  <p class="choice-copy">Toggle on/off</p>
                </div>
              </label>
            </div>
          </div>

          <div class="note-strip">In production: enforce at least 1 cover + 3 photos before ranking higher in search.</div>
        </section>

        <section :id="sectionLinks[12].id" class="panel onboard-panel">
          <div class="section-head">
            <div>
              <h2 class="section-title">Team & assignments (optional)</h2>
              <p class="section-copy">Invite managers, assign roles and primary responsibility.</p>
            </div>
            <div class="status-pill">
              <span class="status-dot"></span>
              Linked to existing place
            </div>
          </div>

          <div class="note-strip">Optional: invite a business manager or venue manager. RBAC controls access; assignments control responsibility routing.</div>

          <div class="section-grid">
            <div class="subpanel">
              <h3 class="subpanel-title">Invite team (optional)</h3>
              <div class="grid gap-4 md:grid-cols-3">
                <label class="field">
                  <span class="field-label">Invite email</span>
                  <input v-model.trim="form.inviteEmail" type="email" class="dark-input" placeholder="e.g., manager@grandpalace.com">
                </label>
                <label class="field">
                  <span class="field-label">Role</span>
                  <input v-model.trim="form.teamRole" type="text" class="dark-input" placeholder="Venue Manager (venue scope)">
                </label>
                <label class="field">
                  <span class="field-label">Assign to venue</span>
                  <input v-model.trim="form.assignVenue" type="text" class="dark-input" placeholder="This venue">
                </label>
              </div>
            </div>

            <div class="subpanel">
              <h3 class="subpanel-title">Responsibility assignment (optional)</h3>
              <div class="grid gap-4 md:grid-cols-2">
                <label class="field">
                  <span class="field-label">Primary venue manager name</span>
                  <input v-model.trim="form.primaryManager" type="text" class="dark-input" placeholder="e.g., John (primary)">
                </label>
                <label class="field">
                  <span class="field-label">Backup manager name</span>
                  <input v-model.trim="form.backupManager" type="text" class="dark-input" placeholder="e.g., Mary (backup)">
                </label>
              </div>
            </div>
          </div>

          <div class="note-strip">If no primary manager is set, the system can default routing to the OWNER.</div>
        </section>

        <section :id="sectionLinks[13].id" class="panel onboard-panel">
          <div class="section-head">
            <div>
              <h2 class="section-title">Review & publish</h2>
              <p class="section-copy">See what's missing. Publish when ready.</p>
            </div>
            <div class="status-pill">
              <span class="status-dot"></span>
              Linked to existing place
            </div>
          </div>

          <div class="subpanel">
            <h3 class="subpanel-title">Readiness summary</h3>
            <div class="summary-box">
              <div>
                <p class="text-2xl font-semibold text-[#111827]">Completion: 57%</p>
                <p class="mt-2 text-sm text-slate-500">Required items must be complete to publish.</p>
              </div>
              <div class="inline-flex items-center gap-2 rounded-full border border-[#f5d7b4] bg-[#fff4e6] px-4 py-2 text-sm text-[#9a5b18]">
                <span class="h-2 w-2 rounded-full bg-[#f5a524]"></span>
                In progress
              </div>
            </div>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <div
              v-for="item in reviewItems"
              :key="item.title"
              class="review-card"
              :class="item.done ? 'review-done' : 'review-pending'"
            >
              <div class="review-icon">
                <i :class="item.done ? 'ri-check-line' : 'ri-error-warning-line'"></i>
              </div>
              <div>
                <p class="choice-title">{{ item.title }}</p>
                <p class="choice-copy">{{ item.copy }}</p>
              </div>
            </div>
          </div>

          <div class="note-strip">Tap an item to jump to that step. Publish is allowed only when required items are complete.</div>

          <div class="mt-6 flex justify-end">
            <button type="submit" class="gradient-btn max-w-[280px]">Publish listing</button>
          </div>
        </section>
        </form>
      </div>
    </section>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, reactive, ref } from 'vue'

const offerOptions = [
  { value: 'venues', label: 'Venues' },
  { value: 'services', label: 'Services' },
  { value: 'both', label: 'Both' }
]

const places = [
  { name: 'Grand Palace Convention Center', meta: '123 Main St, Dallas, TX • 2 business(es) listed here' },
  { name: 'Royal Banquet Complex', meta: '88 Lake Rd, Frisco, TX • 1 business(es) listed here' },
  { name: 'Temple Community Campus', meta: '10 Temple Dr, Irving, TX • 3 business(es) listed here' }
]

const relationshipOptions = ['OWNER', 'TENANT', 'OPERATOR']
const venueTypeOptions = ['Banquet Hall', 'Lawn', 'Rooftop', 'Community Hall']
const eventTypes = ['Wedding', 'Reception', 'Corporate', 'Birthday', 'Religious', 'Conference', 'Community']
const yesNoOptions = ['Yes', 'No']
const alcoholOptions = ['Allowed', 'Restricted', 'Not allowed']
const cateringPolicyOptions = [
  'Inhouse catering, Outside vendors not permitted',
  'Inhouse catering, Outside vendors allowed',
  'No inhouse service, Outside vendors allowed from panel',
  'No inhouse services, outside vendors allowed'
]
const decorPolicyOptions = [
  'Decorators should be chosen only from enlisted Panel',
  'Outside decorators permitted',
  'In-house décor'
]
const parkingTypeOptions = ['Self', 'Valet', 'Mixed']
const decorationTags = ['No open flames', 'No confetti', 'No drilling', 'No fireworks', 'No smoke machines']
const availabilityModes = ['FULL_DAY', 'SLOT_BASED', 'REQUEST_ONLY']
const pricingModels = ['Per Slot', 'Per Event', 'Request Quote']
const depositTypes = ['Percent', 'Fixed']
const userCancellationOptions = [
  'Partial Refund Offered',
  'No Refund Offered',
  'No Refund Offered However Date Adjustment Can Be Done',
  'Full Refund Offered'
]
const hostCancellationOptions = [
  'Partial Refund Offered',
  'No Refund Offered',
  'Full Refund Offered'
]
const roomGuaranteeOptions = [
  'Yes, set number of rooms should be booked to host a wedding',
  'No, can host a wedding without room booking',
  'Complete buyout of the rooms is mandatory'
]
const weddingSpaceOptions = ['Indoor', 'Outdoor', 'Poolside', 'Terrace / Rooftop']
const venueApplicabilityOptions = [
  'Venue is Wheel chair friendly',
  'Venue has sufficient parking available',
  'Venue is veg only (does not serve non veg)',
  'Venue has a beachfront/ beach view',
  'Venue requires complete buyout of all rooms to host a wedding',
  'Venue can host events for <50 people',
  'Venue has a mountain view',
  'Venue has a lake view/ river view',
  'Venue is pet friendly',
  'Venue allows outside decorator',
  'Venue allows outside caterer',
  'Venue allows outside alcohol',
  'Venue has a sea view'
]
const parkingAvailabilityDetailOptions = [
  'There is sufficient parking available',
  'Parking is available near the venue',
  'No parking available'
]
const alcoholDetailedOptions = [
  'In house alcohol available, Outside alcohol permitted',
  'In house alcohol available, Outside alcohol not permitted',
  'In house alcohol not available, Outside alcohol permitted',
  'In house alcohol not available, Outside alcohol not permitted'
]
const djPolicyOptions = [
  'In house DJ available, Outside DJ permitted',
  'In house DJ available, Outside DJ not permitted',
  'In house DJ not available, Outside DJ permitted',
  'In house DJ not available, Outside DJ not permitted'
]
const addonOptions = ['Catering', 'Décor', 'Bar staff', 'Rooms', 'Equipment rental', 'Valet/Parking', 'Security']
const uploadOptions = [
  'Cover photo added',
  'Venue gallery added',
  'Space photos added',
  'Floor plan uploaded (optional)',
  'Menu PDFs uploaded (optional)'
]

const reviewItems = [
  { title: 'Business basics', copy: 'Business name + contact + city/state', done: false },
  { title: 'Place linked', copy: 'Select an existing place or add a new place address', done: true },
  { title: 'Venue listing', copy: 'Venue name + type + description', done: false },
  { title: 'Spaces added', copy: 'At least 1 space. If flexible layout enabled, partitions + layout options must be defined.', done: false },
  { title: 'Availability configured', copy: 'Choose mode + define slot templates (overnight optional) + buffers', done: true },
  { title: 'Pricing configured', copy: 'At least venue default or space/config pricing (or Request Quote)', done: true },
  { title: 'Deposit & cancellation policy', copy: 'Deposit + risk-free window (optional but recommended)', done: true },
  { title: 'Photos (recommended)', copy: 'Add cover + space photos to rank higher', done: false }
]

const sectionLinks = [
  { id: 'create-account', label: 'Create account' },
  { id: 'business-basics', label: 'Business basics' },
  { id: 'link-place', label: 'Link your place' },
  { id: 'venue-listing', label: 'Venue listing' },
  { id: 'rules-logistics', label: 'Rules & logistics' },
  { id: 'add-spaces', label: 'Add spaces' },
  { id: 'availability-setup', label: 'Availability setup' },
  { id: 'pricing-setup', label: 'Pricing setup' },
  { id: 'deposit-cancellation', label: 'Deposit & cancellation' },
  { id: 'additional-information', label: 'Additional information' },
  { id: 'addons-services', label: 'Add-ons & services' },
  { id: 'photos-documents', label: 'Photos & documents' },
  { id: 'team-assignments', label: 'Team & assignments' },
  { id: 'review-publish', label: 'Review & publish' }
]

const activeSection = ref(sectionLinks[0].id)
let observer = null

const form = reactive({
  fullName: '',
  email: '',
  password: '',
  accountPhone: '',
  loginEmailId: '',
  brandName: '',
  offerType: 'both',
  businessDisplayName: '',
  primaryContactName: '',
  contactPersonName: '',
  businessPhone: '',
  businessEmail: '',
  additionalEmailId: '',
  whatsappNumber: '',
  city: '',
  state: '',
  website: '',
  address: '',
  websiteLink: '',
  facebookUrl: '',
  instagramUrl: '',
  youtubeVimeoUrl: '',
  placeMode: 'existing',
  placeSearch: '',
  selectedPlace: 'Temple Community Campus',
  relationshipType: 'TENANT',
  unit: '',
  primaryLocation: true,
  venueName: '',
  venueType: 'Banquet Hall',
  shortDescription: '',
  oneLineVenueBio: '',
  fullDescription: '',
  usp: '',
  publicPhone: '',
  publicEmail: '',
  publicWebsite: '',
  eventTypes: ['Wedding'],
  cateringPolicy: 'No inhouse services, outside vendors allowed',
  outsideCatering: 'Yes',
  inhouseCatering: 'Yes',
  alcoholPolicy: 'Allowed',
  noiseCutoff: '11:00 PM',
  decorPolicy: 'Outside decorators permitted',
  parkingAvailable: 'Yes',
  parkingType: 'Self',
  wheelchairAccessible: 'Yes',
  decorationRestrictions: ['No open flames'],
  internalNotes: '',
  availabilityMode: 'FULL_DAY',
  sharedPricing: false,
  rentalCostWithPlate: 'Yes',
  defaultPricingModel: 'Per Slot',
  basePrice: '',
  vegMenuPrice: '',
  nonVegMenuPrice: '',
  additionalHourRate: '',
  weekendNote: '',
  useVenueDefault: false,
  spacePricingModel: 'Per Slot',
  morningPrice: '',
  eveningPrice: '',
  overnightPrice: '',
  perEventPrice: '',
  spaceAdditionalHourRate: '',
  depositRequired: true,
  depositType: 'Percent',
  depositAmount: '20',
  bookingLeadWeeks: '',
  riskFreeRefund: true,
  fullRefundDays: '30',
  priceLockAfterBooking: true,
  userCancellationPolicy: 'Partial Refund Offered',
  hostCancellationPolicy: 'Full Refund Offered',
  cancellationTerms: '',
  weddingSpaces: [],
  roomGuaranteePolicy: 'No, can host a wedding without room booking',
  basicRoomPrice: '',
  accommodationRoomsAvailable: '',
  roomsIncludedInDestinationPrice: '100',
  smallGatheringsAllowed: 'Yes',
  venueApplicability: [],
  operationsStartYear: '',
  parkingAvailabilityDetail: 'There is sufficient parking available',
  alcoholDetailedPolicy: 'In house alcohol available, Outside alcohol permitted',
  minimumDecorPrice: '',
  djPolicy: 'In house DJ available, Outside DJ permitted',
  addons: ['Catering'],
  uploads: [],
  inviteEmail: '',
  teamRole: '',
  assignVenue: 'This venue',
  primaryManager: '',
  backupManager: ''
})

const submit = () => {
  // eslint-disable-next-line no-console
  console.log('Vendor onboard prototype data', { ...form })
}

onMounted(() => {
  const sections = sectionLinks
    .map((item) => document.getElementById(item.id))
    .filter(Boolean)

  observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
      if (visible.length) activeSection.value = visible[0].target.id
    },
    {
      rootMargin: '-20% 0px -60% 0px',
      threshold: [0.15, 0.35, 0.6]
    }
  )

  sections.forEach((section) => observer.observe(section))
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
    observer = null
  }
})
</script>

<style scoped>
.scrollspy {
  position: sticky;
  top: 24px;
  border: 1px solid #eadfcd;
  border-radius: 20px;
  background: linear-gradient(180deg, #fffdfa 0%, #f8f2e8 100%);
  padding: 18px 14px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.06);
}

.scrollspy-kicker {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #8a6339;
}

.scrollspy-link {
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 0.92rem;
  color: #5b6472;
  text-decoration: none;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.scrollspy-link:hover {
  background: rgba(249, 115, 22, 0.08);
  color: #1f2937;
}

.scrollspy-link.active {
  background: #fff1df;
  color: #1f2937;
  box-shadow: inset 0 0 0 1px rgba(249, 115, 22, 0.18);
}

.scrollspy-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #d0b08c;
  flex-shrink: 0;
}

.scrollspy-link.active .scrollspy-dot {
  background: #f97316;
}

.onboard-panel {
  scroll-margin-top: 24px;
}

.panel {
  border: 1px solid #eadfcd;
  border-radius: 24px;
  background: linear-gradient(180deg, #fffdfa 0%, #f8f2e8 100%);
  overflow: hidden;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.06);
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 18px 14px;
  border-bottom: 1px solid #efe4d4;
}

.section-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #111827;
}

.section-copy {
  margin-top: 4px;
  font-size: 0.95rem;
  color: #6b7280;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #f5d7b4;
  border-radius: 999px;
  background: #fff4e6;
  padding: 8px 14px;
  font-size: 0.9rem;
  color: #9a5b18;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #f5a524;
}

.section-grid {
  display: grid;
  gap: 14px;
  padding: 16px;
}

.subpanel {
  border: 1px solid #efe4d4;
  border-radius: 18px;
  background: #fff;
  padding: 14px;
}

.subpanel-title {
  margin-bottom: 12px;
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
}

.field {
  display: block;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.9rem;
  color: #6b7280;
}

.dark-input {
  width: 100%;
  border: 1px solid #e5d8c7;
  border-radius: 14px;
  background: #fffdf8;
  padding: 12px 14px;
  font-size: 1rem;
  color: #111827;
}

.dark-input:focus {
  outline: none;
  border-color: #f97316;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.12);
}

.dark-input::placeholder {
  color: #9ca3af;
}

.dark-textarea {
  resize: vertical;
  min-height: 90px;
}

.choice-card,
.toggle-card,
.choice-row,
.list-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border: 1px solid #eadfcd;
  border-radius: 16px;
  background: #fffdf9;
  padding: 14px;
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.choice-card.compact {
  min-height: 74px;
}

.choice-card.active,
.toggle-card.active,
.choice-row.active,
.list-card.active {
  border-color: #f97316;
  background: #fff1df;
  box-shadow: inset 0 0 0 1px rgba(249, 115, 22, 0.16);
}

.choice-icon,
.radio-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border: 1.5px solid #e2c6a1;
  border-radius: 4px;
  flex-shrink: 0;
  color: #f97316;
}

.radio-indicator {
  border-radius: 999px;
}

.radio-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: transparent;
}

.choice-row.active .radio-indicator {
  border-color: #f97316;
}

.choice-row.active .radio-dot {
  background: #f97316;
}

.choice-title {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
}

.choice-copy {
  margin-top: 4px;
  font-size: 0.92rem;
  color: #6b7280;
}

.note-strip {
  margin: 0 16px 16px;
  border: 1px dashed #e5d8c7;
  border-radius: 16px;
  background: #fff8ee;
  padding: 12px 14px;
  font-size: 0.92rem;
  color: #7c6a53;
}

.note-strip.inside {
  margin: 0;
}

.mini-btn {
  border: 1px solid #e4c29b;
  border-radius: 14px;
  background: #fff;
  padding: 8px 14px;
  font-size: 0.92rem;
  color: #7c4a1d;
}

.mini-btn.active {
  border-color: #f97316;
  color: #c25b0a;
}

.mini-btn.danger {
  border-color: #f3c0c0;
  color: #dc2626;
}

.gradient-btn {
  width: 100%;
  border: 0;
  border-radius: 16px;
  padding: 14px 18px;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(90deg, #f97316 0%, #f59e0b 100%);
}

.summary-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #eadfcd;
  border-radius: 18px;
  background: #fff;
  padding: 18px;
}

.review-card {
  display: flex;
  gap: 12px;
  border: 1px solid #eadfcd;
  border-radius: 18px;
  background: #fff;
  padding: 14px;
}

.review-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  flex-shrink: 0;
}

.review-done .review-icon {
  color: #34d399;
  border: 1px solid rgba(52, 211, 153, 0.4);
}

.review-pending .review-icon {
  color: #94a3b8;
  border: 1px solid rgba(148, 163, 184, 0.35);
}

@media (max-width: 768px) {
  .section-head,
  .summary-box {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
